import type { RankingControl, RankingControlPage } from '@repo/sdk/gen'
import { zRankingGetControlData } from '@repo/sdk/zod'
import type { DataTablePaginationConfig } from '@repo/ui/components/data-table/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import type { ReactNode } from 'react'
import { useTranslation } from '@/lib/i18n'
import { ListHeader, QUOTA_OF, Rows } from './ranking-list'
import { ExcludedRows, NotAdmittedRows } from './ranking-lists'

/** The lists of a ranking in the order of the tabs, as the endpoints name them. */
export const RANKING_LISTS =
	zRankingGetControlData.shape.body.shape.data.shape.list.unwrap().options

export type RankingListKey = (typeof RANKING_LISTS)[number]

/**
 * The lists of a ranking (of control or provisional) as tabs, each with its
 * rows in the count; the open one is `children`.
 */
function RankingListTabs({
	totals,
	value,
	onValueChange,
	children
}: {
	totals: RankingControl['totals']
	value: RankingListKey
	onValueChange: (value: RankingListKey) => void
	children: ReactNode
}) {
	const { t } = useTranslation()
	return (
		<Tabs
			onValueChange={(next: RankingListKey) => onValueChange(next)}
			value={value}
		>
			<div className='-mx-4 max-w-full overflow-x-auto overflow-y-hidden border-border border-b px-4 md:mx-0 md:px-0'>
				<TabsList variant='line'>
					{RANKING_LISTS.map((key) => (
						<TabsTrigger key={key} value={key}>
							{t(`admin-ranking:control_lists.${key}`)}
							<span className='text-muted-foreground tabular-nums'>
								{totals[key]}
							</span>
						</TabsTrigger>
					))}
				</TabsList>
			</div>
			<TabsContent className='pt-4' value={value}>
				{children}
			</TabsContent>
		</Tabs>
	)
}

/**
 * One page of one list: what the list gives above its rows, then the table of
 * the rows of its kind. While the list asked for loads, the rows of the one
 * before are not shown under it. With `residenceId` a name opens the
 * application.
 */
function RankingListPage({
	list,
	page,
	seats,
	unfilled,
	pagination,
	loading,
	residenceId
}: {
	/** The list asked for. */
	list: RankingListKey
	/** The page the last answer carries, of this list or of the one before. */
	page: RankingControlPage
	seats: RankingControl['seats']
	/** The seats of each quota nobody took, when the page knows them. */
	unfilled?: { q30: number; q70: number }
	pagination: DataTablePaginationConfig
	loading: boolean
	residenceId?: string
}) {
	const { t } = useTranslation()
	const shown: RankingControlPage =
		page.list === list ? page : { list, rows: [] }
	const paging = { pagination, loading: loading || page.list !== list }
	return (
		<div className='flex flex-col gap-3'>
			{shown.list === 'not_admitted' && (
				<NotAdmittedRows
					items={shown.rows}
					residenceId={residenceId}
					{...paging}
				/>
			)}
			{shown.list === 'excluded' && (
				<ExcludedRows
					items={shown.rows}
					residenceId={residenceId}
					{...paging}
				/>
			)}
			{shown.list === 'q70_dsu_seat' && (
				<p className='text-muted-foreground text-sm'>
					{t('admin-ranking:lists.q70_dsu_seat.subtitle')}
				</p>
			)}
			{shown.list !== 'not_admitted' &&
				shown.list !== 'excluded' &&
				shown.list !== 'q70_dsu_seat' && (
					<ListHeader
						kind={shown.list}
						seats={seats[shown.list]}
						unfilled={unfilled?.[QUOTA_OF[shown.list]]}
					/>
				)}
			{shown.list !== 'not_admitted' && shown.list !== 'excluded' && (
				<Rows residenceId={residenceId} rows={shown.rows} {...paging} />
			)}
		</div>
	)
}

export { RankingListPage, RankingListTabs }
